'use client';

import { useCallback, useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PanelShell, useIsMd } from './panel-shell';
import { MyContentManager } from './my-content-manager';
import { PostsTab, ChannelsTab } from './library-tabs';
import { AppsView, AgentsView } from './marketplace-views';
import { ChatsTab } from './recent-tabs';
import { SpacesView } from './spaces-view';
import { CollaborateView } from './collaborate-view';
import { AccountView } from './account-view';
import { DockView } from './dock-view';
import { MarketplaceFeed } from '@/components/marketplace-feed';
import { PostDetailView } from '@/components/posts/post-detail-view';
import { CreatePostForm, CreateChannelForm } from '@/components/create/create-forms';
import { usePanelBackStack } from './use-panel-back';
import { SearchView } from './search-view';
import { NotificationsView } from '@/components/notifications/notifications-view';
import { openWorkspaceCreateDialog } from '@/components/workspace/create-dialog';
import { openPlansDialog } from '@/components/plans-dialog-host';
import { GuestSaveBanner } from '@/components/workspace/guest-save-banner';
import { openCreateWithDialog } from '@/components/workspace/create-with-dialog';
import { useWorkspace } from '@/components/workspace/store';
import { emitWorkspaceListChanged } from '@/lib/workspaces/list-events';
import { PANEL_ICON_BTN, PANEL_CREATE_BADGE } from './panel-ui';
import {
  subscribeWorkspacePanelOpen,
  useWorkspacePanelTab,
  getWorkspacePanelTab,
  notifyWorkspacePanelTab,
  closeWorkspacePanel,
  requestContentCreate,
  subscribePostOpen,
} from './panel-events';
import { useRailV2 } from '@/lib/workspaces/rail-flag';

/** The scoped "+ create" action per view (null = no create here). */
function createActionFor(view: Tab): (() => void) | null {
  switch (view) {
    case 'apps':
      return () => openCreateWithDialog('app');
    case 'agents':
      return () => openCreateWithDialog('agent');
    case 'feed':
      return () => openWorkspaceCreateDialog('post');
    case 'content':
      return () => requestContentCreate();
    default:
      return null;
  }
}

type Tab =
  | 'apps'
  | 'agents'
  | 'workspaces'
  | 'content'
  | 'feed'
  | 'search'
  | 'notifications'
  | 'collaborate'
  | 'dock'
  | 'account'
  | 'chats'
  | 'posts'
  | 'channels';

const VIEW_TITLES: Record<Tab, string> = {
  workspaces: 'Workspaces',
  apps: 'Apps',
  agents: 'Agents',
  content: 'Content',
  feed: 'Feed',
  search: 'Search',
  notifications: 'Notifications',
  collaborate: 'Collaborate',
  dock: 'Dock',
  account: 'Account',
  chats: 'Chats',
  posts: 'Posts',
  channels: 'Channels',
};


/**
 * My Library — the workspace's unified explorer. Four tabs share a
 * common shell (header + tab pills) and each tab owns its data, its
 * filter state, and its row rendering. Filters are server-side via
 * the shared `FilterRail` primitive.
 *
 * The Posts and Channels tabs are conditional — they only appear when
 * the user has at least one item to show. We probe each endpoint with
 * `limit=1` on mount; a zero result hides the corresponding pill (and
 * resets the active tab if the user is currently looking at it).
 *
 * The Content tab is the canonical file manager: folders, breadcrumbs,
 * upload, quota, drag-to-folder, context menu — replacing the
 * legacy `/content` page once we deprecate it.
 */
export function LeftPanel() {
  const t = useT();
  const [tab, setTab] = useState<Tab>('workspaces');
  const railV2 = useRailV2();
  const router = useRouter();
  const [hasPosts, setHasPosts] = useState(false);
  const [hasChannels, setHasChannels] = useState(false);
  // `mounted` gates the conditional Posts / Channels tab pills so the
  // server-rendered HTML (which always has them hidden because both
  // probes start as `false`) matches the very first client render
  // (where the same is true). The probes still run on mount and flip
  // the flags asynchronously — by the time those updates arrive,
  // we've already committed at least one client render with `mounted
  // === true`, so React reconciles the conditional buttons in a
  // normal update rather than a hydration warning.
  //
  // Without this gate, certain refresh cycles ended up shipping an
  // SSR'd output with the probe results baked in (e.g. when the page
  // was served from cache after a previous tab had already populated
  // them), and the first client render — starting from `false` —
  // disagreed with the server. React then discarded the entire SSR
  // tree which (a) wasted work, and (b) raced with global event
  // listeners in surrounding components.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Under the rail the active view is the SHARED mirror the rail writes
  // (single source of truth), not this panel's local `tab` — so the lit rail
  // icon and the panel body can't disagree. Default to Workspaces, and seed the
  // mirror on mount so a reload with the panel already open lights the right
  // rail icon. Legacy chrome (`!railV2`) keeps the local-`tab` + tab-strip path.
  const sharedTab = useWorkspacePanelTab('left');
  const activeView: Tab = railV2 ? ((sharedTab as Tab | undefined) ?? 'workspaces') : tab;
  const isMd = useIsMd();

  // In-panel post drill-down. Clicking a post in the Feed pushes its id onto a
  // LIFO stack and the panel body swaps to a `PostDetailView` (comments, edit,
  // moderation) with a Back button — mirroring the channel drill-down instead of
  // popping the read-only `/preview/post` modal. Quoted posts push deeper via
  // `onOpenPost`; browser Back pops ONE level via `usePanelBackStack`.
  const [postStack, setPostStack] = useState<string[]>([]);
  // The post id that should open straight into edit mode (a Feed card's
  // "Edit" drills into the detail view already editing).
  const [autoEditId, setAutoEditId] = useState<string | null>(null);
  const pushPost = useCallback(
    (id: string) => setPostStack((s) => (s[s.length - 1] === id ? s : [...s, id])),
    [],
  );
  const popPost = useCallback(() => {
    setPostStack((s) => s.slice(0, -1));
    setAutoEditId(null);
  }, []);
  const editPost = useCallback(
    (id: string) => {
      pushPost(id);
      setAutoEditId(id);
    },
    [pushPost],
  );
  const currentPostId = postStack[postStack.length - 1];
  const isPostDetail = !!currentPostId;
  usePanelBackStack(postStack.length, popPost);
  // Lets surfaces outside this panel (the shared-post preview dialog) drill
  // into a thread, since the stack lives here. Held as `pendingPostId` rather
  // than pushed straight away: switching to the Feed view clears the stack
  // (the effect below), so the push has to happen once we're already there.
  const [pendingPostId, setPendingPostId] = useState<string | null>(null);
  useEffect(
    () =>
      subscribePostOpen((id) => {
        notifyWorkspacePanelTab('left', 'feed');
        setPendingPostId(id);
      }),
    [],
  );
  // Inline create — the Feed view's "+ Create" swaps the panel body for the
  // post/channel composer (same drill pattern as a post detail) instead of
  // popping the fullscreen create dialog.
  const [createKind, setCreateKind] = useState<'post' | 'channel' | null>(null);
  usePanelBackStack(createKind ? 1 : 0, () => setCreateKind(null));
  // The Feed's inner tab (Activity | Channels), mirrored up so "+ Create"
  // opens the matching composer: post on Activity, channel on Channels.
  const [feedTab, setFeedTab] = useState<'activity' | 'channels'>('activity');
  // Leaving the Feed view (rail switch, etc.) abandons any drilled post or
  // open composer so the user never returns to a stale view elsewhere.
  useEffect(() => {
    setPostStack([]);
    setAutoEditId(null);
    setCreateKind(null);
  }, [activeView]);
  // Declared after the reset above so it wins within the same commit: drill
  // into the requested thread once the Feed view is actually showing.
  useEffect(() => {
    if (activeView !== 'feed' || !pendingPostId) return;
    setPostStack([pendingPostId]);
    setPendingPostId(null);
  }, [activeView, pendingPostId]);
  useEffect(() => {
    if (railV2 && !getWorkspacePanelTab('left')) {
      notifyWorkspacePanelTab('left', 'workspaces');
    }
  }, [railV2]);

  const createWorkspace = async () => {
    try {
      const res = await fetch('/api/workspaces', { method: 'POST' });
      if (!res.ok) {
        // Per-plan workspace cap → open the Plans dialog instead of
        // silently no-oping (matches create-fab + workspaces-tab).
        if (res.status === 403) {
          const j = await res.clone().json().catch(() => null);
          if (j?.code === 'upgrade_required') openPlansDialog();
        }
        return;
      }
      const data = await res.json();
      if (data?.workspace?.id) {
        emitWorkspaceListChanged();
        router.push(`/workspace/${data.workspace.id}`);
      }
    } catch {
      // network/gate error — ignore
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    void fetch('/api/users/me/posts?limit=1', { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (ctrl.signal.aborted || !data) return;
        const items = (data.items ?? []) as unknown[];
        setHasPosts(items.length > 0);
      })
      .catch(() => {});
    void fetch('/api/users/me/channels', { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (ctrl.signal.aborted || !data) return;
        const channels = (data.channels ?? []) as unknown[];
        setHasChannels(channels.length > 0);
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  // If the active tab gets hidden (e.g. a probe finishes after first
  // render with zero rows, or the user navigates here on a clean
  // account), fall back to Agents which is always available.
  useEffect(() => {
    if (tab === 'posts' && !hasPosts) setTab('agents');
    if (tab === 'channels' && !hasChannels) setTab('agents');
  }, [tab, hasPosts, hasChannels]);

  // Honour `openWorkspacePanel('left', { tab })` — e.g. the user menu's
  // "Workspaces" link opens the panel on the Workspaces tab.
  useEffect(() => {
    return subscribeWorkspacePanelOpen((detail) => {
      if (detail.id !== 'left' || !detail.tab) return;
      if (
        detail.tab === 'apps' ||
        detail.tab === 'agents' ||
        detail.tab === 'workspaces' ||
        detail.tab === 'content' ||
        detail.tab === 'chats' ||
        detail.tab === 'posts' ||
        detail.tab === 'channels'
      ) {
        setTab(detail.tab);
      }
    });
  }, []);

  return (
    <PanelShell
      edge="left"
      id="left"
      ariaLabel={t('workspace.canvas.panels.left')}
      defaultSize={440}
      minSize={300}
      maxSize={760}
      desktopHandle="button"
      closedLabel="Library"
      noClosedHandle={railV2}
      attached={railV2}
      trapBack
    >
      <div className="flex flex-col h-full">
        {/* The mobile strip is GONE: the bottom bar is mobile navigation
            (Discover · Screens · Alerts · You), Discover absorbs the browse
            views, and Stage carries the switcher — this sheet now only hosts
            the views the bar opens (Alerts, Account) plus desktop panels. */}
        {railV2 ? (
          // ONE header row per view: title · view actions · close. All actions
          // (create / settings / share) use the same icon-button language and
          // sit in the header — never colliding with the close button.
          <div className="flex items-center gap-1 px-3 pt-3 pb-1.5 select-none">
            <span className="flex-1 truncate text-[17px] font-semibold tracking-tight">
              {VIEW_TITLES[activeView]}
            </span>
            {activeView === 'workspaces' && (
              // Per-workspace settings / share / rename now live on each row's
              // hover ⋯ menu (matches the wireframe); the header keeps create.
              <button
                type="button"
                onClick={() => void createWorkspace()}
                aria-label={t('workspace.canvas.newWorkspace')}
                title={t('workspace.canvas.newWorkspace')}
                className={PANEL_CREATE_BADGE}
              >
                <Plus className="h-3.5 w-3.5" />
                Create
              </button>
            )}
            {(() => {
              // Feed creates inline (post/channel composer swaps into the
              // panel body) and follows the inner tab: Activity → post,
              // Channels → channel. Other views keep their create flows.
              const create =
                activeView === 'feed'
                  ? () => setCreateKind(feedTab === 'channels' ? 'channel' : 'post')
                  : createActionFor(activeView);
              return create ? (
                <button
                  type="button"
                  onClick={create}
                  aria-label={`New ${VIEW_TITLES[activeView]}`}
                  title={`New ${VIEW_TITLES[activeView]}`}
                  className={PANEL_CREATE_BADGE}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Create
                </button>
              ) : null;
            })()}
            {/* Desktop only: on mobile the sheet's own close X (outside its
                right edge) is the close affordance, so the header carries none. */}
            {isMd && (
              <button
                type="button"
                onClick={() => closeWorkspacePanel('left')}
                aria-label={t('workspace.panels.closePanel')}
                title={t('workspace.canvas.node.close')}
                className={PANEL_ICON_BTN}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="px-3 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground select-none">
              {t('workspace.canvas.panels.left')}
            </div>
            <div className="flex items-center gap-1 overflow-x-auto px-2 pt-2 select-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TabButton active={tab === 'workspaces'} onClick={() => setTab('workspaces')}>
                Workspaces
              </TabButton>
              <TabButton active={tab === 'apps'} onClick={() => setTab('apps')}>
                Apps
              </TabButton>
              <TabButton active={tab === 'agents'} onClick={() => setTab('agents')}>
                Agents
              </TabButton>
              <TabButton active={tab === 'content'} onClick={() => setTab('content')}>
                Content
              </TabButton>
              <TabButton active={tab === 'chats'} onClick={() => setTab('chats')}>
                Chats
              </TabButton>
              {mounted && hasPosts && (
                <TabButton active={tab === 'posts'} onClick={() => setTab('posts')}>
                  Posts
                </TabButton>
              )}
              {mounted && hasChannels && (
                <TabButton
                  active={tab === 'channels'}
                  onClick={() => setTab('channels')}
                >
                  Channels
                </TabButton>
              )}
            </div>
          </>
        )}

        <div className="min-h-0 flex-1 flex flex-col">
          {createKind ? (
            // Inline composer — replaces the fullscreen create dialog for the
            // Feed. Same drill pattern as the post detail: Back returns to
            // the feed, and a segmented toggle switches Post / Channel.
            <div className="min-h-0 flex-1 flex flex-col">
              <div className="flex items-center gap-2 px-3 py-2">
                <button
                  type="button"
                  onClick={() => setCreateKind(null)}
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </button>
                <div className="ml-auto flex gap-1">
                  <button
                    type="button"
                    onClick={() => setCreateKind('post')}
                    className={cn(
                      'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                      createKind === 'post'
                        ? 'bg-foreground text-background'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    Post
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreateKind('channel')}
                    className={cn(
                      'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                      createKind === 'channel'
                        ? 'bg-foreground text-background'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    Channel
                  </button>
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
                {createKind === 'post' ? (
                  <CreatePostForm onSuccess={() => setCreateKind(null)} />
                ) : (
                  <CreateChannelForm onSuccess={() => setCreateKind(null)} />
                )}
              </div>
            </div>
          ) : isPostDetail ? (
            // Drilled into a post — swap the whole body for the detail view. The
            // panel title stays "Feed" (same as a drilled channel keeps the
            // "Feed" title), and this in-body Back bar returns one level.
            <div className="min-h-0 flex-1 flex flex-col">
              <div className="flex items-center px-3 py-2">
                <button
                  type="button"
                  onClick={popPost}
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 @container">
                <PostDetailView
                  key={currentPostId}
                  postId={currentPostId}
                  embedded
                  onBack={popPost}
                  onOpenPost={pushPost}
                  autoEdit={currentPostId === autoEditId}
                />
              </div>
            </div>
          ) : (
            <>
              {activeView === 'search' && <SearchView />}
              {activeView === 'notifications' && (
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {/* Panel header already shows the "Notifications" title, so the
                      view renders just its bulk actions + list (no double title). */}
                  <NotificationsView showTitle={false} dense />
                </div>
              )}
              {activeView === 'apps' && <AppsView />}
              {activeView === 'agents' && <AgentsView />}
              {activeView === 'collaborate' && <CollaborateView />}
              {activeView === 'dock' && <DockView />}
              {activeView === 'account' && <AccountView />}
              {activeView === 'workspaces' && <SpacesView />}
              {activeView === 'content' && <MyContentManager />}
              {activeView === 'feed' && (
                <div className="min-h-0 flex-1 overflow-y-auto px-1">
                  <MarketplaceFeed
                    hideSideTrending
                    panelTabs
                    initialTab="activity"
                    onOpenPost={pushPost}
                    onEdit={editPost}
                    onTabChange={setFeedTab}
                  />
                </div>
              )}
              {activeView === 'chats' && <ChatsTab />}
              {activeView === 'posts' && hasPosts && <PostsTab />}
              {activeView === 'channels' && hasChannels && <ChannelsTab />}
            </>
          )}
        </div>
        {/* Guest conversion nudge at the foot of every view except Account
            (which carries its own dedicated guest CTAs). Self-hides for
            non-guests and once dismissed. */}
        {activeView !== 'account' && <GuestSaveBanner />}
      </div>
    </PanelShell>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 px-2 py-1 text-xs rounded-md transition-colors',
        active
          ? 'bg-foreground text-background'
          : 'text-muted-foreground hover:text-foreground hover:bg-foreground/8',
      )}
    >
      {children}
    </button>
  );
}
